import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

type Props = {
  show: boolean
  onClose: () => void
  // Optional: when provided, an edge chevron handle re-opens the panel while it
  // is collapsed.
  onOpen?: () => void
  theme?: string
  children: React.ReactNode
}

const DetailsSlidePanel: React.FC<Props> = ({
  show,
  onClose,
  onOpen,
  theme,
  children,
}) => {
  // Keep the panel out of the DOM while closed so it never sits off-screen and
  // causes horizontal overflow. `mounted` keeps it around for the slide-out
  // transition; `active` drives the open/close transform.
  const [mounted, setMounted] = useState(show)
  const [active, setActive] = useState(false)

  // The component is rendered deep inside the form (so its fields stay wired to
  // the form's state), but the panel must behave as the modal's first child:
  // anchored to .modal-content, not the Bootstrap .modal-body it is nested in
  // (which is position:relative and starts below the header). So we portal the
  // markup into a host node inserted as the FIRST child of the nearest
  // .modal-content. The inline marker below is only used to locate it.
  // `resolved` flips once we've decided where to render (portal vs inline
  // fallback for any non-modal usage), so we never flash in the wrong place.
  const markerRef = useRef<HTMLSpanElement>(null)
  const [host, setHost] = useState<HTMLElement | null>(null)
  const [resolved, setResolved] = useState(false)
  // The nearest .modal-content, resolved once — the open-handle portals here
  // even while the panel itself is unmounted.
  const [modalContent, setModalContent] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setModalContent(markerRef.current?.closest('.modal-content') as HTMLElement | null)
  }, [])

  useEffect(() => {
    if (!mounted) {
      setResolved(false)
      return
    }
    const modalContentEl = markerRef.current?.closest('.modal-content') as HTMLElement | null
    if (!modalContentEl) {
      // Not inside a modal — render inline where the component sits.
      setHost(null)
      setResolved(true)
      return
    }
    const node = document.createElement('div')
    node.className = 'details-slide-host'
    modalContentEl.insertBefore(node, modalContentEl.firstChild)
    setHost(node)
    setResolved(true)
    return () => {
      node.remove()
      setHost(null)
    }
  }, [mounted])

  useEffect(() => {
    if (show) {
      setMounted(true)
      // Wait two frames so the browser paints the closed state before we
      // flip to open, otherwise the slide-in transition is skipped.
      let inner = 0
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setActive(true))
      })
      return () => {
        cancelAnimationFrame(outer)
        cancelAnimationFrame(inner)
      }
    }
    // Closing: play the slide-out transition, then unmount.
    setActive(false)
    const timer = setTimeout(() => setMounted(false), 250)
    return () => clearTimeout(timer)
  }, [show])

  const panel = (
    <>
      <div
        className={`details-slide-backdrop ${active ? 'details-slide-backdrop--open' : ''}`}
        onClick={onClose}
      />
      <aside
        className={`details-slide-panel vh-100 ${active ? 'details-slide-panel--open' : ''} ${
          theme === 'dark' ? 'theme__div modal__bg' : ''
        }`}
        aria-hidden={!active}
      >
        <div className='details-slide-panel__header'>
          {/* Collapse chevron — the sole close control; slides the panel back
              out to the right. */}
          <span
            className='details-slide-panel__chevron cursor-pointer'
            onClick={onClose}
            role='button'
            aria-label='Collapse details'
            title='Collapse'
          >
            <i className={`bi bi-chevron-right fs-4 ${theme === 'dark' ? 'white__text' : 'text-dark'}`} />
          </span>
          <h4 className={`m-0 ${theme === 'dark' ? 'white__text' : 'text-dark'}`}>Details</h4>
        </div>
        <div className='details-slide-panel__body'>{children}</div>
      </aside>
    </>
  )

  // Edge handle to re-open the panel — pull it in from the right (chevron-left).
  const openHandle = (
    <button
      type='button'
      className={`details-slide-open-handle ${theme === 'dark' ? 'theme__div' : ''}`}
      onClick={onOpen}
      aria-label='Open details'
      title='Details'
    >
      <i className='bi bi-chevron-left' />
    </button>
  )

  return (
    <>
      {/* Inline marker — never rendered visibly; used only to find .modal-content. */}
      <span ref={markerRef} style={{ display: 'none' }} aria-hidden />
      {mounted && resolved && (host ? createPortal(panel, host) : panel)}
      {!mounted && onOpen && modalContent && createPortal(openHandle, modalContent)}
    </>
  )
}

export default DetailsSlidePanel
